/**
 * US2046691 — normalizes the two VDP gallery widgets' widgetUserEvent
 * vocabularies into one semantic interaction.
 *
 * Stellantis sites run one of two gallery widgets, split roughly 50/50 and
 * selected by the VDP page-layout id in the account's config (`SECTIONS*`
 * layouts render ws-vehicle-gallery, plain `RESPONSIVE*` layouts render
 * ws-vehicle-media). They emit different `element`/`result`/`action` strings for
 * the same user action, so every mapping rule would otherwise need a
 * per-widget duplicate. Adding a third generation should be a table row here,
 * not another set of rules.
 *
 * `currentItem`/`totalItems` reach ws-tagging flattened onto eventData: both
 * widgets pass them inside `dataToExcludeFromWidgetUserEvent`, which DDC strips
 * from the GTM push but forwards to ws-tagging.
 */

// Exported so other rules that need to recognize a gallery widget by name
// (e.g. the legacy-CTA suppression rule in event/default.js) share this one
// table instead of keeping a second, driftable copy of the widget list.
export const GALLERY_WIDGETS = new Set([
	'ws-vehicle-media',
	'ws-vehicle-gallery'
]);

// Each entry matches on `element`, on `result`, or on both; an omitted field is
// a wildcard. `element` is the primary key where it is unique per control.
// `result` is required where one element serves two meanings — the inline photo
// also fires for dynamic HTML gallery content (not an image click), and the
// fullscreen toggle reuses one label for both directions. Zoom is keyed on
// `result` ALONE because its handler forwards a caller-supplied element.
// Matching is case-insensitive: the two widgets disagree on the casing of
// otherwise identical labels ("Next button" vs "Next Button"), which is also
// why there is no separate ws-vehicle-gallery next/previous row.
const INTERACTIONS = [
	// ws-vehicle-media
	{ element: 'photo carousel', result: 'opens gallery modal', kind: 'image' },
	{ element: 'photo', result: 'opens gallery modal', kind: 'image' },
	{
		element: 'photo count button',
		result: 'opens gallery modal',
		kind: 'image'
	},
	{
		element: 'thumbnail carousel',
		result: 'photo comes into view',
		kind: 'image'
	},
	{ element: 'photo carousel next button', kind: 'next' },
	{ element: 'photo carousel previous button', kind: 'previous' },
	{ element: 'modal gallery next button', kind: 'next' },
	{ element: 'modal gallery previous button', kind: 'previous' },
	{ element: 'modal gallery close button', kind: 'close' },
	// ws-vehicle-media only: the controls the deck boxes as "Photo Gallery
	// Controls" (p.8). ws-vehicle-gallery's modal has neither.
	{ result: 'zooms into photo', kind: 'zoomIn' },
	{ result: 'zooms out of photo', kind: 'zoomOut' },
	{
		element: 'fullscreen button',
		result: 'enters fullscreen mode',
		kind: 'fullscreenEnter'
	},
	{
		element: 'fullscreen button',
		result: 'exits fullscreen mode',
		kind: 'fullscreenExit'
	},
	// ws-vehicle-gallery
	{
		element: 'on page gallery photo',
		result: 'open gallery modal',
		kind: 'image'
	},
	{
		element: 'photo carousel thumbnail',
		result: 'zoom into photo',
		kind: 'image'
	},
	{ element: 'photo carousel close button', kind: 'close' }
];

const normalize = (value) =>
	typeof value === 'string' ? value.trim().toLowerCase() : undefined;

export function getGalleryInteraction(eventData) {
	const {
		widgetName,
		element,
		result,
		carouselDirection,
		currentItem,
		totalItems
	} = eventData || {};

	if (!GALLERY_WIDGETS.has(widgetName)) {
		return null;
	}

	const elementKey = normalize(element);
	const resultKey = normalize(result);

	if (!elementKey && !resultKey) {
		return null;
	}

	// An omitted field is a wildcard, so an entry must still be selective: a
	// result-only entry (zoom) matches any element. Beware the near-collision
	// between ws-vehicle-media's zoom (`zooms into photo`) and
	// ws-vehicle-gallery's thumbnail (`zoom into photo`) — one letter apart, and
	// they map to different kinds. Do not "tidy" either string.
	const match = INTERACTIONS.find(
		(entry) =>
			(entry.element === undefined || entry.element === elementKey) &&
			(entry.result === undefined || entry.result === resultKey)
	);

	// ws-vehicle-gallery renders NO arrow buttons on mobile (measured at 390px
	// and 414px), so swipe is its only mobile pagination. It reports
	// `carouselDirection` on both swipes and arrow clicks, so one
	// direction-based fallback covers mobile without matching on `result`
	// strings whose photo number varies. ws-vehicle-media reports no direction
	// on a swipe, so its swipes stay unmapped by design; its mobile arrows do
	// exist but the Next one is overlapped by the Share anchor at mobile widths,
	// so mobile arrow interaction there is not reliably reachable either.
	if (!match) {
		const direction = normalize(carouselDirection);

		if (
			widgetName === 'ws-vehicle-gallery' &&
			(direction === 'next' || direction === 'previous')
		) {
			return { kind: direction, index: currentItem, totalItems };
		}

		return null;
	}

	return { kind: match.kind, index: currentItem, totalItems };
}

export default getGalleryInteraction;
